/**
 * De weekplanner van de betaalde bot.
 *
 * Zeven kolommen, één per dag, met daarin de betaalde posts die op die dag
 * staan. Een post versleep je naar een andere dag of een ander tijdvak; dat
 * slaat meteen op via `POST /paid-posting/schedule`.
 *
 * Twee dingen die de planner zelf bewaakt, omdat het platform ze afdwingt:
 *
 * * **FanCentro wil 24 uur voorbereiding.** Een dag- of tijdvak dat daarbinnen
 *   valt is niet aan te klikken en niet te bedropen. De server schuift een te
 *   vroege post anders vooruit, en dan staat er iets anders op het scherm dan
 *   wat je net hebt neergelegd.
 * * **Niet twee posts op precies hetzelfde moment.** Twee uploads tegelijk in
 *   dezelfde vault leverden eerder dubbele items op; de planner zet ze in dat
 *   geval een kwartier uit elkaar.
 */
import { useMemo, useState } from 'react'
import { Tag } from './ui'
import { api } from '../lib/api'
import { useActions } from '../lib/actions'
import { num } from '../lib/format'

export interface PlanPost {
  id: number
  file: string
  title: string
  post_kind: string
  publish_at: string
  status: string
  fc_unlock_credits?: number | null
}

/** De uren waarop gepland kan worden. Overdag en 's avonds; 's nachts posten
 *  levert nauwelijks verkopen op en maakt het lastig te controleren. */
const UREN = [9, 12, 15, 18, 21]

const FC_AANLOOP_UREN = 24

function dagen(vanaf: Date): Date[] {
  const uit: Date[] = []
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(vanaf)
    d.setDate(vanaf.getDate() + i)
    d.setHours(0, 0, 0, 0)
    uit.push(d)
  }
  return uit
}

function sleutel(d: Date, uur: number): string {
  return `${d.toISOString().slice(0, 10)}T${String(uur).padStart(2, '0')}`
}

export function Weekplanner({ posts, onGewijzigd }: {
  posts: PlanPost[]
  onGewijzigd: () => void
}) {
  const { notify } = useActions()
  const [weekOffset, setWeekOffset] = useState(0)
  const [sleept, setSleept] = useState<number | null>(null)
  const [bezig, setBezig] = useState(false)

  const vanaf = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() + weekOffset * 7)
    d.setHours(0, 0, 0, 0)
    return d
  }, [weekOffset])

  const week = useMemo(() => dagen(vanaf), [vanaf])
  const vroegst = useMemo(
    () => new Date(Date.now() + FC_AANLOOP_UREN * 3600 * 1000), [])

  /** Wat er in welk vak staat. Een post zonder tijd staat in de kolom ernaast. */
  const inVak = useMemo(() => {
    const kaart = new Map<string, PlanPost[]>()
    for (const p of posts) {
      if (!p.publish_at) continue
      const d = new Date(p.publish_at)
      if (Number.isNaN(d.getTime())) continue
      // Naar het dichtstbijzijnde tijdvak, zodat een post van 13:07 zichtbaar
      // wordt in het vak van 12 uur in plaats van te verdwijnen.
      const uur = UREN.reduce((a, u) =>
        Math.abs(u - d.getHours()) < Math.abs(a - d.getHours()) ? u : a, UREN[0])
      const s = sleutel(d, uur)
      kaart.set(s, [...(kaart.get(s) ?? []), p])
    }
    return kaart
  }, [posts])

  const ongepland = useMemo(() => posts.filter((p) => !p.publish_at), [posts])

  async function verplaats(postId: number, dag: Date, uur: number) {
    const wanneer = new Date(dag)
    wanneer.setHours(uur, 0, 0, 0)
    if (wanneer < vroegst) {
      notify('FanCentro needs 24 hours to prepare; pick a later time.', false)
      return
    }
    // Staat er al iets, dan een kwartier ernaast: twee uploads op hetzelfde
    // moment in dezelfde vault gaven eerder dubbele items.
    const bezet = (inVak.get(sleutel(dag, uur)) ?? []).length
    wanneer.setMinutes(bezet * 15)
    setBezig(true)
    try {
      await api.post('/api/admin/paid-posting/schedule', {
        post_id: postId,
        publish_at: wanneer.toISOString(),
      })
      onGewijzigd()
    } catch (fout) {
      notify(`Scheduling failed: ${fout instanceof Error ? fout.message : fout}`, false)
    } finally {
      setBezig(false)
      setSleept(null)
    }
  }

  async function planHeleWeek() {
    setBezig(true)
    try {
      const uit = await api.post<{ scheduled?: number }>(
        '/api/admin/paid-posting/schedule-all')
      notify(`${num(uit?.scheduled ?? 0)} post(s) spread over the week.`)
      onGewijzigd()
    } catch (fout) {
      notify(`Scheduling failed: ${fout instanceof Error ? fout.message : fout}`, false)
    } finally {
      setBezig(false)
    }
  }

  return (
    <>
      <div className="filters">
        <button className="btn ghost sm" type="button"
                onClick={() => setWeekOffset((n) => n - 1)}>←</button>
        <b style={{ fontSize: 13 }}>
          {week[0].toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
          {' – '}
          {week[6].toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
        </b>
        <button className="btn ghost sm" type="button"
                onClick={() => setWeekOffset((n) => n + 1)}>→</button>
        {weekOffset !== 0 ? (
          <button className="btn ghost sm" type="button"
                  onClick={() => setWeekOffset(0)}>This week</button>
        ) : null}
        <span className="spacer" style={{ flex: 1 }} />
        <span className="mut2" style={{ fontSize: 12 }}>
          {num(ongepland.length)} unscheduled
        </span>
        <button className="btn primary sm" type="button"
                disabled={bezig || ongepland.length === 0}
                onClick={planHeleWeek}>
          Plan whole week
        </button>
      </div>

      <div className="xscroll">
      <div className="weekraster">
        <div className="uurkolom">
          <div className="kop" />
          {UREN.map((u) => (
            <div key={u} className="uur">{String(u).padStart(2, '0')}:00</div>
          ))}
        </div>
        {week.map((d) => (
          <div key={d.toISOString()} className="dagkolom">
            <div className="kop">
              {d.toLocaleDateString('nl-NL', { weekday: 'short' })}{' '}
              <span className="mut2">{d.getDate()}</span>
            </div>
            {UREN.map((u) => {
              const wanneer = new Date(d)
              wanneer.setHours(u, 0, 0, 0)
              const teVroeg = wanneer < vroegst
              const items = inVak.get(sleutel(d, u)) ?? []
              return (
                <div
                  key={u}
                  className={teVroeg ? 'vak tevroeg' : 'vak'}
                  title={teVroeg
                    ? 'Within 24 hours — FanCentro needs more preparation'
                    : undefined}
                  onDragOver={(e) => { if (!teVroeg) e.preventDefault() }}
                  onDrop={(e) => {
                    e.preventDefault()
                    if (!teVroeg && sleept != null) verplaats(sleept, d, u)
                  }}
                >
                  {items.map((p) => (
                    <div key={p.id} className="planpost" draggable
                         onDragStart={() => setSleept(p.id)}
                         onDragEnd={() => setSleept(null)}
                         title={p.title || p.file}>
                      <div className="naam">{p.title || p.file}</div>
                      <div className="row" style={{ gap: 4 }}>
                        <Tag kind="mut">{p.post_kind}</Tag>
                        {p.fc_unlock_credits
                          ? <Tag kind="acc">{p.fc_unlock_credits} credits</Tag> : null}
                      </div>
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        ))}
      </div>
      </div>

      {ongepland.length ? (
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="head"><h3>Not scheduled yet</h3></div>
          <div className="body">
            <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
              {ongepland.map((p) => (
                <div key={p.id} className="planpost los" draggable
                     onDragStart={() => setSleept(p.id)}
                     onDragEnd={() => setSleept(null)}>
                  <div className="naam">{p.title || p.file}</div>
                  <Tag kind="mut">{p.post_kind}</Tag>
                </div>
              ))}
            </div>
            <div className="mut2" style={{ fontSize: 12, marginTop: 8 }}>
              Drag a post to a day and time, or use "Plan whole week".
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
